import { downloadText } from './download';
import { apiGet, apiPost, apiPut, planningPath } from '../services/PlanningApiClient';

export interface UserRating {
  id: string;
  vendorId: string;
  vendorName: string;
  userName: string;
  userEmail: string;
  rating: number;
  comments: string;
  timestamp: string;
}

export const saveRating = async (rating: Omit<UserRating, 'id'>): Promise<void> => {
  try {
    // Add new rating with unique ID
    const newRating: UserRating = {
      ...rating,
      id: `rating_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    // Save back to localStorage for immediate access
    const existingRatings = localStorage.getItem('user_ratings');
    const parsed = existingRatings ? JSON.parse(existingRatings) : [];
    const ratings: UserRating[] = Array.isArray(parsed) ? parsed : [];
    ratings.push(newRating);
    localStorage.setItem('user_ratings', JSON.stringify(ratings));

    // Also attempt to save to GuestGuru API
    try {
      await saveRatingToApi(newRating);
    } catch (error) {
      console.warn('Could not save rating to API, saved to localStorage only:', error);
    }

  } catch (error) {
    console.error('Error saving rating:', error);
    throw new Error('Failed to save rating');
  }
};

// Save rating to GuestGuru API endpoint
const saveRatingToApi = async (rating: UserRating): Promise<void> => {
  try {
    await apiPost(planningPath('/vendor-ratings'), {
      vendor_id: rating.vendorId || null,
      vendor_name: rating.vendorName,
      user_name: rating.userName || null,
      user_email: rating.userEmail || null,
      rating: rating.rating,
      comments: rating.comments || null,
    });
  } catch (error) {
    throw new Error(`Failed to save rating to API: ${error}`);
  }
};

export const getRatings = async (): Promise<UserRating[]> => {
  try {
    // Try to fetch from API first
    try {
      const response = await apiGet<{ ratings: any[] }>(planningPath('/vendor-ratings'));
      if (response && response.ratings) {
        return response.ratings.map(r => ({
          id: `rating_${r.id}`,
          vendorId: r.vendor_id,
          vendorName: r.vendor_name,
          userName: r.user_name,
          userEmail: r.user_email,
          rating: r.rating,
          comments: r.comments,
          timestamp: r.created_at,
        }));
      }
    } catch (apiError) {
      console.warn('Could not fetch ratings from API, falling back to localStorage:', apiError);
    }

    // Fallback to localStorage if API unavailable
    const ratings = localStorage.getItem('user_ratings');
    if (!ratings) return [];
    const parsedFallback = JSON.parse(ratings);
    return Array.isArray(parsedFallback) ? parsedFallback : [];
  } catch (error) {
    console.error('Error loading ratings:', error);
    return [];
  }
};

export const getRatingsForVendor = async (vendorId: string): Promise<UserRating[]> => {
  const allRatings = await getRatings();
  return allRatings.filter(rating => rating.vendorId === vendorId);
};

export const exportRatingsToJSON = async (): Promise<void> => {
  try {
    const ratings = await getRatings();
    const dataStr = JSON.stringify(ratings, null, 2);
    downloadText('user_ratings.json', dataStr, 'application/json');
  } catch (error) {
    console.error('Error exporting ratings:', error);
    throw new Error('Failed to export ratings');
  }
};
